import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { formatCurrency, formatNumber } from "@/lib/format";

const METRICS = {
  omzet_incl_btw: { label: "Omzet", color: "#0ea5e9", format: formatCurrency },
  brutomarge: { label: "Brutomarge", color: "#10b981", format: formatCurrency },
  aantal_orders: { label: "Aantal orders", color: "#7c3aed", format: formatNumber },
  gemiddelde_orderwaarde: { label: "Ø Orderwaarde", color: "#f59e0b", format: formatCurrency },
};

function FitBoundsToData({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = points
      .filter((p) => p.lat != null && p.lon != null)
      .map((p) => [p.lat, p.lon]);
    if (bounds.length) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

export default function RegionAnalysis() {
  const [jaar, setJaar] = useState("alle");
  const [jaren, setJaren] = useState([]);
  const [metric, setMetric] = useState("omzet_incl_btw");
  const [data, setData] = useState({ regions: [], landen: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/dashboard/jaren").then(({ data }) => setJaren(data || [])); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (jaar && jaar !== "alle") params.jaar = parseInt(jaar);
    api.get("/analytics/regions", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [jaar]);

  const mappable = useMemo(() => data.regions.filter((r) => r.lat != null && r.lon != null), [data]);
  const maxValue = useMemo(() => Math.max(1, ...mappable.map((r) => r[metric] || 0)), [mappable, metric]);
  const m = METRICS[metric];

  return (
    <div className="space-y-6" data-testid="regions-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Geografie</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mt-1">Regioanalyse</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Postcodes worden privacy-vriendelijk geclusterd. Bel-postcode prefix bepaalt de bubble-positie.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-full sm:w-44 h-9" data-testid="region-metric-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(METRICS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={jaar} onValueChange={setJaar}>
            <SelectTrigger className="w-full sm:w-32 h-9" data-testid="region-jaar-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle jaren</SelectItem>
              {jaren.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.landen.map((l) => (
          <Card key={l.land} className="bg-white border-slate-200 p-5 rounded-lg">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">{l.land === "BE" ? "België" : l.land === "NL" ? "Nederland" : l.land}</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">{formatCurrency(l.omzet_incl_btw)}</p>
            <p className="text-xs text-slate-500 mt-1">{formatNumber(l.aantal_orders)} orders · marge {formatCurrency(l.brutomarge)}</p>
          </Card>
        ))}
      </div>

      <Card className="bg-white border-slate-200 rounded-lg overflow-hidden" data-testid="region-map-card">
        <div className="p-5 pb-3">
          <h2 className="text-xl font-semibold text-slate-900">Kaart · {m.label}</h2>
        </div>
        <div className="h-[360px] sm:h-[480px] w-full relative z-0" data-testid="region-map">
          <MapContainer center={[51.0, 5.0]} zoom={7} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; OSM'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />
            <FitBoundsToData points={mappable} />
            {mappable.map((r, i) => {
              const value = r[metric] || 0;
              const radius = 6 + Math.sqrt(value / maxValue) * 30;
              return (
                <CircleMarker
                  key={`${r.postcode}-${r.woonplaats}-${i}`}
                  center={[r.lat, r.lon]}
                  radius={radius}
                  pathOptions={{ color: m.color, fillColor: m.color, fillOpacity: 0.45, weight: 1.5 }}
                >
                  <Popup>
                    <div className="text-xs space-y-0.5">
                      <p className="font-semibold text-slate-900">{r.postcode} {r.woonplaats}</p>
                      <p className="text-slate-500">{r.land}</p>
                      <p>Orders: <span className="font-medium">{formatNumber(r.aantal_orders)}</span></p>
                      <p>Omzet: <span className="font-medium">{formatCurrency(r.omzet_incl_btw)}</span></p>
                      <p>Marge: <span className="font-medium">{formatCurrency(r.brutomarge)}</span></p>
                      <p>Klanten: <span className="font-medium">{formatNumber(r.unieke_klanten)}</span></p>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 rounded-lg overflow-hidden">
        <div className="p-5 pb-3">
          <h2 className="text-xl font-semibold text-slate-900">Top regio&apos;s</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="regions-table">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                <th className="text-left py-3 px-4 font-semibold">Postcode</th>
                <th className="text-left py-3 px-4 font-semibold">Woonplaats</th>
                <th className="text-left py-3 px-4 font-semibold">Land</th>
                <th className="text-right py-3 px-4 font-semibold">Orders</th>
                <th className="text-right py-3 px-4 font-semibold">Klanten</th>
                <th className="text-right py-3 px-4 font-semibold">Omzet</th>
                <th className="text-right py-3 px-4 font-semibold">Marge</th>
                <th className="text-right py-3 px-4 font-semibold">Ø order</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="text-center text-slate-400 py-8">Laden…</td></tr>}
              {data.regions.slice(0, 100).map((r, i) => (
                <tr key={`${r.postcode}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-2.5 px-4 font-medium text-slate-900">{r.postcode}</td>
                  <td className="py-2.5 px-4 text-slate-700">{r.woonplaats}</td>
                  <td className="py-2.5 px-4 text-slate-500">{r.land}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums">{formatNumber(r.aantal_orders)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-600">{formatNumber(r.unieke_klanten)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-900">{formatCurrency(r.omzet_incl_btw)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-emerald-700 font-medium">{formatCurrency(r.brutomarge)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-600">{formatCurrency(r.gemiddelde_orderwaarde)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
